import {ClientEvent} from '@udemy/event-tracking';
import {idFromGlobalId} from '@udemy/graphql';
import {
    SubscriptionPlan,
    SubscriptionPlanPricingOptionItem,
    SubscriptionPlanProductType,
} from '@udemy/subscription-graphql';

const getIdFromGlobalId = (planId: string) => {
    const intPlanId = parseInt(planId, 10);
    return isNaN(intPlanId) ? idFromGlobalId(planId) : intPlanId;
};

const parseGlobalIdOrPlanPriceId = (id: string) => {
    try {
        return getIdFromGlobalId(id); // For the new store the id is will be an integer 74
    } catch {
        return parseInt(id, 10);
    }
};

// RenewalIntervalType as adopted for the event tracking system
type RenewIntervalType = 'month' | 'day' | 'year' | 'week';

/** The expected SubscriptionPlan data for a SubsciptionOfferPresented event */
interface SubscriptionPlanPricingOption {
    price: string;
    listPrice: string;
    currency: string;
    renewalIntervalType: RenewIntervalType;
    renewalIntervalCount: number;
    hasTrial: boolean;
    trialDays?: number | null;
}

/**
 * Event fired when a user sees a subscription offer for a personal plan
 */
export class SubscriptionOfferPresented extends ClientEvent {
    primaryPricingOption: SubscriptionPlanPricingOption;
    allPricingOptions: SubscriptionPlanPricingOption[];
    productType: 'consumer' | 'team' | 'consumersubscription_value_plan';
    countryCode: string;
    courseId: number | null;
    courseTrackingId: string | null;
    badges: string[] | null;
    uiRegion: string;

    constructor({
        subscriptionPlan,
        productType,
        countryCode,
        courseId = null,
        courseTrackingId = null,
        badges = null,
        uiRegion,
    }: {
        subscriptionPlan: SubscriptionPlan;
        productType: SubscriptionPlanProductType;
        countryCode: string;
        courseId?: number | null;
        courseTrackingId?: string | null;
        badges?: string[] | null;
        uiRegion: string;
    }) {
        super('SubscriptionOfferPresented');
        const pricingOptions = this.formatPricingOptions(subscriptionPlan);

        this.primaryPricingOption = pricingOptions[0];
        this.allPricingOptions = pricingOptions;
        this.productType = this.mapProductType(productType);
        this.countryCode = countryCode;
        this.courseId = courseId;
        this.courseTrackingId = courseTrackingId;
        this.badges = badges;
        this.uiRegion = uiRegion;
    }

    private mapProductType(productType: SubscriptionPlanProductType) {
        switch (productType) {
            case SubscriptionPlanProductType.Team:
                return 'team' as const;
            case SubscriptionPlanProductType.ConsumersubscriptionValuePlan:
                return 'consumersubscription_value_plan' as const;
            default:
                return 'consumer' as const;
        }
    }

    private formatPricingOptions(subscriptionPlan: SubscriptionPlan) {
        return subscriptionPlan.priceOptions.map((option: SubscriptionPlanPricingOptionItem) => ({
            price: option.listPrice.amount,
            listPrice: option.listPrice.amount,
            currency: option.listPrice.currency as string,
            renewalIntervalType: option.renewalInterval.type.toLowerCase() as RenewIntervalType,
            renewalIntervalCount: option.renewalInterval.count,
            hasTrial: !!option.trial?.dateInterval,
            trialDays: option.trial?.dateInterval?.count ?? null,
        }));
    }
}

/**
 * Event fired when a clicks to learn more about a specific subscription plan
 */
export class SubscriptionLearnMoreEvent extends ClientEvent {
    planPriceId: string;
    planType: SubscriptionPlanProductType;
    uiRegion: string;
    planPriceIds?: number[];

    constructor({
        planId,
        planType,
        uiRegion,
        planPriceIds = null,
    }: {
        planId: string;
        planType: SubscriptionPlanProductType;
        uiRegion: string;
        planPriceIds?: string[] | null;
    }) {
        super('SubscriptionLearnMoreEvent');
        this.planPriceId = parseGlobalIdOrPlanPriceId(planId).toString();
        this.planType = planType;
        this.uiRegion = uiRegion;
        this.planPriceIds = planPriceIds?.map((planPriceId) => getIdFromGlobalId(planPriceId));
    }
}
